import { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Accordion,
  ControlledDropDown,
  ControlledMultiSelect,
  CustomRadioGroup,
  DropdownType,
  SkeletonLoader,
} from '@ui-kit'
import {
  ENTITY_FIELDS,
  ENTITY_FIELDS_META,
  ENTITY_NAME,
  HYBRID_LAYOUT_CONSTANTS,
} from '../../../utils/constants'
import {
  INSURANCE_STATUS_OPTIONS_CONSTANT,
  MEDICAID_STATUS_OPTIONS_CONSTANT,
  VA_BENEFITS_APPLIED_OPTIONS_CONSTANT,
} from './customFieldConstant'
import { STRINGS } from '../../../utils/strings'
import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
} from '@app-store'
import {
  mergeNestedObjects,
  normalizeLookups,
} from '../../../utils/hybridFormRendererfuntions.utils'

const keys = {
  insurance_radiogroup: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.insurance_status}`,
  insurance_dropdown: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.insurance_type}`,
  millitary_radiogroup: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.va_benefits_applied}`,
  medicaid_radiogroup: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.medicaid_status}`,
  indicate_third_party_insurance: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.INDICATE_THIRD_PARTY_INSURANCE}`,
}

const CustomInsurance = ({
  dataId,
  entityId,
  mode,
}: {
  dataId: string
  entityId: string
  mode: string | undefined
}) => {
  const { register, control, setValue, getValues } = useFormContext()
  const insurance_status = useWatch({ name: keys?.insurance_radiogroup })
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    if (!dataId) return
    setLoading(true)
    const payload: GetGenericEntityListingApiType = {
      entity_id: entityId,
      select: [ENTITY_NAME.CASE_PAP_DETAILS],
      page: 0,
      size: 25,
      related_entities: {
        [ENTITY_NAME.CASE_PAP_DETAILS]: {
          select: [
            ENTITY_FIELDS.CASE_PAP_DETAILS.insurance_status,
            ENTITY_FIELDS.CASE_PAP_DETAILS.insurance_type,
            ENTITY_FIELDS.CASE_PAP_DETAILS.va_benefits_applied,
            ENTITY_FIELDS.CASE_PAP_DETAILS.medicaid_status,
            ENTITY_FIELDS.CASE_PAP_DETAILS.INDICATE_THIRD_PARTY_INSURANCE,
          ],
          criteria: [
            {
              criteria: [],
            },
          ],
        },
      },
      filters: [
        {
          criteria: [],
        },
      ],
    }
    try {
      const response = await getGenericEntityListingAPI(payload, dataId)

      if (response) {
        const normalized = normalizeLookups(response)
        const val = getValues()
        const endData = mergeNestedObjects(val, normalized)
        setValue(
          `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}`,
          {
            ...val[
              `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}`
            ],
            ...endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
              ENTITY_NAME.CASE_PAP_DETAILS
            ],
          }
        )
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (loading) return
    if (insurance_status === INSURANCE_STATUS_OPTIONS_CONSTANT.NO_INSURANCE) {
      setValue(keys.insurance_dropdown, [])
      setValue(keys.millitary_radiogroup, '')
      setValue(keys.medicaid_radiogroup, '')
      setValue(keys.indicate_third_party_insurance, '')
    }
  }, [insurance_status, loading])

  if (loading) {
    return <SkeletonLoader rows={4} columns={4} />
  }

  return (
    <Accordion
      heading={
        <div>
          <span>{STRINGS.INSURANCE_INFORMATION}</span>
        </div>
      }
      isDefaultActive={true}
      isConditional
    >
      <div className="p-3 h-[18.7rem] text-sm">
        <div>
          {mode === 'view' ? (
            <label htmlFor="">
              {getValues(keys.insurance_radiogroup) ?? '--'}
            </label>
          ) : (
            <CustomRadioGroup
              options={
                ENTITY_FIELDS_META.CASE_PAP_DETAILS.insurance_status.options
              }
              name={keys.insurance_radiogroup}
              register={register}
            />
          )}

          <div className="py-2">
            {mode === 'view' ? (
              <div>
                <span>{STRINGS.INSURANCE_TYPE}</span>
                {getValues(keys.insurance_dropdown) == null ||
                getValues(keys.insurance_dropdown)?.length === 0 ? (
                  <p className="text-xs font-extraLight">--</p>
                ) : (
                  <ul className="list-disc list-inside">
                    {getValues(keys.insurance_dropdown)?.map(
                      (option: string) => (
                        <li>{option}</li>
                      )
                    )}
                  </ul>
                )}
              </div>
            ) : (
              <ControlledMultiSelect
                control={control}
                name={keys.insurance_dropdown}
                label={STRINGS.INSURANCE_TYPE}
                options={
                  ENTITY_FIELDS_META.CASE_PAP_DETAILS.insurance_type.option
                }
                disabled={
                  insurance_status !==
                  INSURANCE_STATUS_OPTIONS_CONSTANT.HAS_INSURANCE
                }
                onChange={(e: DropdownType<string>[]) => {
                  const valuesArray = e.map((item) => item.value)
                  setValue(keys.insurance_dropdown, valuesArray)
                }}
              />
            )}
          </div>
          <div className="pt-2">
            <span>{STRINGS.VA_OR_MILITARY}</span>
            {mode === 'view' ? (
              <p className="text-xs font-extraLight">
                {getValues(keys.millitary_radiogroup) ?? '--'}
              </p>
            ) : (
              <CustomRadioGroup
                options={[
                  {
                    value: VA_BENEFITS_APPLIED_OPTIONS_CONSTANT.YES,
                    label: VA_BENEFITS_APPLIED_OPTIONS_CONSTANT.YES,
                  },
                  {
                    value: VA_BENEFITS_APPLIED_OPTIONS_CONSTANT.NO,
                    label: VA_BENEFITS_APPLIED_OPTIONS_CONSTANT.NO,
                  },
                ]}
                name={keys.millitary_radiogroup}
                register={register}
                disabled={
                  insurance_status !==
                  INSURANCE_STATUS_OPTIONS_CONSTANT.HAS_INSURANCE
                }
              />
            )}
          </div>
          <div className="py-2">
            <span>{STRINGS.MEDICAID_STATUS_QUESTION}</span>
            {mode === 'view' ? (
              <p className="text-xs font-extraLight">
                {getValues(keys.medicaid_radiogroup) ?? '--'}
              </p>
            ) : (
              <CustomRadioGroup
                options={[
                  {
                    value: MEDICAID_STATUS_OPTIONS_CONSTANT.NOT_APPLIED,
                    label: MEDICAID_STATUS_OPTIONS_CONSTANT.NOT_APPLIED,
                  },
                  {
                    value: MEDICAID_STATUS_OPTIONS_CONSTANT.DENIED,
                    label: MEDICAID_STATUS_OPTIONS_CONSTANT.DENIED,
                  },
                  {
                    value: MEDICAID_STATUS_OPTIONS_CONSTANT.PENDING,
                    label: MEDICAID_STATUS_OPTIONS_CONSTANT.PENDING,
                  },
                ]}
                name={keys.medicaid_radiogroup}
                register={register}
                disabled={
                  insurance_status !==
                  INSURANCE_STATUS_OPTIONS_CONSTANT.HAS_INSURANCE
                }
              />
            )}
          </div>

          <div className="py-2">
            {mode === 'view' ? (
              <div>
                <span className="">
                  {STRINGS.INDICATE_THIRD_PARTY_INSURANCE}
                </span>

                <p className="text-xs font-extraLight mt-1">
                  {getValues(keys.indicate_third_party_insurance) ?? '--'}
                </p>
              </div>
            ) : (
              <ControlledDropDown
                name={keys.indicate_third_party_insurance}
                control={control}
                label={STRINGS.INDICATE_THIRD_PARTY_INSURANCE}
                options={
                  ENTITY_FIELDS_META.CASE_PAP_DETAILS
                    .indicate_third_party_insurance.options
                }
                disabled={
                  insurance_status !==
                  INSURANCE_STATUS_OPTIONS_CONSTANT.HAS_INSURANCE
                }
                onChange={(e: DropdownType<string>) => {
                  setValue(keys.indicate_third_party_insurance, e.value)
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Accordion>
  )
}

const InsuranceInformationComponent = ({
  dataId,
  entityId,
  mode,
}: {
  dataId: string
  entityId: string
  mode: string | undefined
}) => {
  return (
    <div>
      <CustomInsurance dataId={dataId} entityId={entityId} mode={mode} />
    </div>
  )
}

export default InsuranceInformationComponent
